import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import {
  DataGridTable,
  GridAction,
  ConfirmationPopup,
  notifyToast,
  ToastType,
  FormPage
} from "../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../apis/api-index";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../store/store-selector";
import { AppUtility } from "../../../utils/utility";
import { saveMarketSector } from "../api-handler/api-handler";

const MarketSectorListForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);

  /**************************************************
  //Form state variables
  **************************************************/
  const [marketSectorRecordList, setMarketSectorRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [totalMarketSector, setTotalMarketSector] = useState(0);
  const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);


  /**************************************************
  //Event definitions
  **************************************************/

  //on click of add icon for creating market sector
  const handleCreateMarketSectorClick = () => {
    navigate("/marketSector");
  };

  //onclick of edit market sector
  const handleEditMarketSectorClick = async (param) => {
    let _navigationData = param.recordResponse;
    delete _navigationData.CreatedDate;
    delete _navigationData.ModifiedDate;
    delete _navigationData.CountryCode;
    delete _navigationData.BusinessCode;

    navigate(
      {
        pathname: "/marketSector",
        search: createSearchParams({
          SectorId: param.recordResponse.SectorId,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  //onclick of confirm of delete popup
  const handleConfirm = async () => {
    let _filterClause = `&sectorid=${selectedRow.id}`;
    await fetchAvailableGrossBranch(_filterClause, selectedRow);
    setPopupOpen(false);
    setSelectedRow(null);
  };

  //on click of cancel of delete popup
  const handleCancel = () => {
    setSelectedRow(null);
    setPopupOpen(false);
  };

  //onClick of delete in list
  const showDeleteMarketSectorPopup = (event) => {
    try {
      setSelectedRow(event);
      setPopupOpen(true);
    } catch (err) {
      alert(err);
    }
  };

  //on page change
  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  //show review modal
  const handleShowSelectedMarketSecChanges = (event) => {
    let _navigationData = { previousPath: "/marketsectorlist" };
    navigate(
      {
        pathname: "/reviewdetails",
        search: createSearchParams({
          reviewFilter: `&locationkey=${event.id}`,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  /**************************************************
  //Fetch API & utility
  **************************************************/


  const setMarketSectorList = (marketSectorList) => {
    try {
      marketSectorList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
      let _marketSectorList = marketSectorList.map((item) => {
        return {
          id: item.SectorId,
          sectorName: item.SectorName,
          recordResponse: item,
        };
      });

      setMarketSectorRecordList(_marketSectorList);
      setTotalMarketSector(_marketSectorList.length);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

  const FetchMarketSectorList = async () => {
    try {
      let _lists = await PricingPortalAPI.getList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getMarketSectorList",
        "marketsector"
      );
      setMarketSectorList(_lists);
    } catch (err) {
      alert(err);
    }
  };
  //Fetch API for checking available gross branch with the selected market sector
  const fetchAvailableGrossBranch = async (filter, param) => {
    try {
      let _availableGrossBranch = await PricingPortalAPI.getFilterList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getGrossBranchList",
        "grossbranch",
        filter
      );
      if (_availableGrossBranch && _availableGrossBranch.length > 0) {
        notifyToast("Gross branch combination exists with this market sector", ToastType.ERROR);
      } else {
        await deleteMarketSector(param);
      }
    } catch (e) {
      alert(e);
    }
  };

  const formPreviousDataForDelete = (param) => {
    try {
      let _previousState = param.recordResponse;
      _previousState.Deleted = true;
      delete _previousState.CreatedDate;
      delete _previousState.ModifiedDate;
      delete _previousState.CountryCode;
      delete _previousState.BusinessCode;
      delete _previousState.Version;
      delete _previousState.SectorId;
      delete _previousState.Status;
      return _previousState ? _previousState : null;
    } catch (e) {
      alert(e);
    }
  };

  //Delete API call
  const deleteMarketSector = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);
      let EditMarketSectorId = param.id;
      const status = await saveMarketSector(formPreviousDataForDelete(param), {
        EditMarketSectorId,
        settingsConfig
      });

      if (status?.responseStatus === 200) {
        notifyToast("Sent for Approval", ToastType.SUCCESS);
        await FetchMarketSectorList();
      } else {
        notifyToast(status.errorMessage ? status.errorMessage : "Market Sector deletion failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //JSX delcaration
  **************************************************/

  //data grid schema
  const dataGridSchema = [
    {
      field: "sectorName",
      headerName: t("marketSectorList.sectorName"),
      flex: 4,
    },
    {
      field: "recordResponse",
      hide: true,
    },
    {
      field: "action",
      headerName: t,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <GridAction actionsConfig={actionConfigs} row={params.row} />
        );
      },
    },
  ];

  //button configs for add and download list
  const buttonConfigs = [
    {
      buttonText: <AddIcon />,
      buttonType: "filled",
      onClick: handleCreateMarketSectorClick,
      buttonColor: "#A62B23",
      textColor: "#fff",
      action: "create"
    },
    {
      buttonText: <SystemUpdateAltIcon />,
      buttonType: "outlined",
      onClick: () => {
        AppUtility.handleDownload(marketSectorRecordList, "marketsectorlist.xlsx");
      },
      buttonColor: "#A62B23",
      textColor: "#A62B23",
      action: "download"
    },
  ];

  //action configs for edit and delete record
  const actionConfigs = [
    {
      onClick: handleShowSelectedMarketSecChanges,
      action: "review"
    },
    {
      icon: MdCreate,
      onClick: handleEditMarketSectorClick,
      buttonColor: "#EEEEE",
      action: "edit"
    },
    {
      icon: MdDelete,
      onClick: showDeleteMarketSectorPopup,
      buttonColor: "#A62B23",
      action: "delete"
    },
  ];


  /**************************************************
  //Form on load useEffect
  **************************************************/
  useEffect(() => {

    spinnerCtx.setLoadingIndicator(true);
    FetchMarketSectorList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={marketSectorRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalMarketSector}
        toolbarHeader={t("marketSectorList.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
      />
      <ConfirmationPopup
        isOpen={popupOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        confirmationText={t("popup.deleteMarketSectorConfirmationMessage")}
        eventObj={selectedRow}
      />
    </FormPage>
  );
};

export default MarketSectorListForm;
